// Escolher a unidade pagadora — e entender que a escolha não se propaga.
//
// Um município é mais de uma folha. Morada Nova tem a prefeitura raiz, a A.M.T e
// o I.M.A.M.N., e as três pagam servidores diferentes com catálogos diferentes.
// A matrícula `1300016` existe na A.M.T e no I.M.A.M.N., com PESSOAS DIFERENTES.
//
// Daí as três decisões deste componente:
//
//  1. A hierarquia é visível. Filial aparece indentada sob a matriz, e não como
//     mais um item de uma lista plana onde "Morada Nova" e "I.M.A.M.N." parecem
//     coisas do mesmo nível.
//  2. O estado de cada unidade aparece SEM seleção — é o que `travaAtiva` na
//     lista de prefeituras passou a permitir.
//  3. Não existe seleção múltipla. A ADR-0005 descartou ação em lote, e o texto
//     diz por quê em vez de deixar o operador procurando o botão.

import { useMemo, useState } from "react";
import { Pill } from "@atlas/ui/web";

export interface Unidade {
  id: number;
  nome: string;
  prefeituraPaiId?: number | null;
  travaAtiva?: boolean;
  /** A classificação institucional (feature 009). Ausentes em resposta anterior
   *  à 009 — a linha então mostra só o nome, como antes. */
  sigla?: string | null;
  rotuloTipo?: string;
}

export interface SeletorDeUnidadeProps {
  unidades: Unidade[];
  valor: number | null;
  onChange: (id: number | null) => void;
  carregando?: boolean;
}

/** Matrizes com suas filiais. Órfãs (mãe fora da lista) entram como raiz. */
function agrupar(unidades: Unidade[], busca: string) {
  const q = busca.trim().toLowerCase();
  const casa = (u: Unidade) => !q || u.nome.toLowerCase().includes(q) || String(u.id).includes(q);

  const porId = new Map(unidades.map((u) => [u.id, u]));
  const filhasDe = new Map<number, Unidade[]>();
  const raizes: Unidade[] = [];

  for (const u of unidades) {
    const paiId = u.prefeituraPaiId ?? null;
    if (paiId != null && porId.has(paiId)) {
      filhasDe.set(paiId, [...(filhasDe.get(paiId) ?? []), u]);
    } else {
      raizes.push(u);
    }
  }

  // Uma matriz permanece visível quando alguma filial dela casa com a busca:
  // esconder a mãe deixaria a filial órfã na tela, sem o contexto que diz de
  // qual município ela é.
  return raizes
    .map((raiz) => ({ raiz, filiais: (filhasDe.get(raiz.id) ?? []).filter(casa) }))
    .filter(({ raiz, filiais }) => casa(raiz) || filiais.length > 0)
    .sort((a, b) => a.raiz.nome.localeCompare(b.raiz.nome, "pt-BR"));
}

export function SeletorDeUnidade({ unidades, valor, onChange, carregando }: SeletorDeUnidadeProps) {
  const [busca, setBusca] = useState("");
  const grupos = useMemo(() => agrupar(unidades, busca), [unidades, busca]);
  const ligadas = unidades.filter((u) => u.travaAtiva).length;

  return (
    <div style={{ display: "grid", gap: 10 }}>
      <label style={{ display: "grid", gap: 4, maxWidth: 420, fontSize: 13 }}>
        Buscar unidade
        <input
          value={busca}
          onChange={(e) => setBusca(e.target.value)}
          placeholder="nome do município ou da autarquia"
          style={{
            padding: 8,
            borderRadius: 8,
            border: "1px solid var(--border)",
            background: "var(--surface)",
            color: "var(--text)",
          }}
        />
      </label>

      <p style={{ margin: 0, fontSize: 13, color: "var(--text-muted)" }}>
        {carregando
          ? "Carregando unidades…"
          : ligadas === 0
            ? "Nenhuma unidade com a trava ligada."
            : `${ligadas} unidade(s) com a trava ligada.`}
      </p>

      <div
        role="radiogroup"
        aria-label="Unidade pagadora"
        style={{
          display: "grid",
          gap: 2,
          maxHeight: 280,
          overflowY: "auto",
          border: "1px solid var(--border)",
          borderRadius: 8,
          padding: 6,
        }}
      >
        {grupos.length === 0 && (
          <p style={{ margin: 8, color: "var(--text-muted)", fontSize: 13 }}>
            {carregando ? "Carregando…" : "Nenhuma unidade encontrada com esse termo."}
          </p>
        )}

        {grupos.map(({ raiz, filiais }) => (
          <div key={raiz.id}>
            <LinhaUnidade
              unidade={raiz}
              nivel={0}
              selecionada={valor === raiz.id}
              onChange={onChange}
              matriz
            />
            {filiais
              .slice()
              .sort((a, b) => a.nome.localeCompare(b.nome, "pt-BR"))
              .map((f) => (
                <LinhaUnidade
                  key={f.id}
                  unidade={f}
                  nivel={1}
                  selecionada={valor === f.id}
                  onChange={onChange}
                />
              ))}
          </div>
        ))}
      </div>

      <p style={{ margin: 0, fontSize: 13, color: "var(--text-muted)" }}>
        A trava é <strong>da unidade</strong> e não herda em direção nenhuma: ligar na matriz não liga nas
        autarquias, e ligar numa autarquia não liga na matriz nem nas irmãs. Não há ação em lote —{" "}
        <strong>de propósito</strong>: aplicar a várias de uma vez é a forma mais rápida de ligar tudo sem
        olhar.
      </p>
    </div>
  );
}

function LinhaUnidade({
  unidade,
  nivel,
  selecionada,
  onChange,
  matriz = false,
}: {
  unidade: Unidade;
  nivel: number;
  selecionada: boolean;
  onChange: (id: number) => void;
  matriz?: boolean;
}) {
  return (
    <label
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        padding: "6px 8px",
        paddingLeft: 8 + nivel * 20,
        borderRadius: 6,
        cursor: "pointer",
        background: selecionada ? "color-mix(in srgb, var(--accent) 14%, transparent)" : undefined,
      }}
    >
      <input
        type="radio"
        name="unidade-pagadora"
        checked={selecionada}
        onChange={() => onChange(unidade.id)}
      />
      <span style={{ flex: 1 }}>
        {unidade.nome}{" "}
        {/* O TIPO NO LUGAR DE "matriz/filial" (feature 009, T046). Os dois
            rótulos antigos diziam a posição na hierarquia e nada sobre o que a
            unidade é — e "filial" é exatamente o vocabulário que esta feature
            existe para tirar: uma autarquia não é filial de ninguém, ela é uma
            entidade própria cuja folha alguém processa. */}
        <span style={{ color: "var(--text-dim)", fontSize: 12 }}>
          {unidade.sigla ? `${unidade.sigla} · ` : ""}
          {unidade.rotuloTipo ?? (matriz ? "unidade pagadora" : "atendida")} · id {unidade.id}
        </span>
      </span>
      {unidade.travaAtiva && <Pill variant="rejeitada">LIGADA</Pill>}
    </label>
  );
}
